/**
 * Newsletter Page - The Daily Edge
 *
 * Displays daily newsletter content with sports betting insights,
 * recap of yesterday's picks, and today's featured plays.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  NewspaperIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  TrophyIcon,
  FireIcon,
  ChartBarIcon,
  BoltIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon
} from '@heroicons/react/24/solid';
import newsletterService from '../services/newsletterService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Section Component - Renders individual newsletter sections
 */
function NewsletterSection({ section }) {
  const getSectionIcon = (type) => {
    const icons = {
      'results_summary': TrophyIcon,
      'what_worked': CheckCircleIcon,
      'what_missed': XCircleIcon,
      'learning_corner': ChartBarIcon,
      'edge_alerts': BoltIcon,
      'streak_watch': FireIcon,
      'confidence_tiers': ChartBarIcon,
      'game_of_the_day': TrophyIcon,
      'tonights_slate': CalendarIcon
    };
    return icons[type] || NewspaperIcon;
  };

  const Icon = getSectionIcon(section.type);

  return (
    <div className="bg-bg-secondary rounded-xl p-6 mb-4 border border-border/50">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-accent-blue/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-accent-blue" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary">{section.title}</h3>
        {section.is_ai && (
          <span className="text-[10px] px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full">
            AI Generated
          </span>
        )}
      </div>
      <div
        className="prose prose-invert prose-sm max-w-none"
        dangerouslySetInnerHTML={{ __html: section.html || section.markdown }}
      />
    </div>
  );
}

/**
 * Stats Card Component
 */
function StatsCard({ label, value, subvalue, color = 'blue' }) {
  const colorClasses = {
    blue: 'text-accent-blue',
    green: 'text-accent-green',
    red: 'text-accent-red',
    yellow: 'text-yellow-500'
  };

  return (
    <div className="bg-bg-secondary rounded-xl p-4 border border-border/50">
      <div className="text-[11px] text-text-secondary uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>{value}</div>
      {subvalue && <div className="text-xs text-text-secondary mt-1">{subvalue}</div>}
    </div>
  );
}

/**
 * Subscribe Form Component
 */
function SubscribeForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const { currentUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const response = await newsletterService.subscribe(email);
      setResult({ success: true, message: response.message });
      setEmail('');
    } catch (error) {
      setResult({
        success: false,
        message: error.response?.data?.detail || 'Failed to subscribe'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-accent-blue/20 to-purple-500/20 rounded-xl p-6 border border-border/50">
      <div className="flex items-center gap-3 mb-3">
        <EnvelopeIcon className="w-6 h-6 text-accent-blue" />
        <h3 className="text-lg font-semibold text-text-primary">Get The Daily Edge in Your Inbox</h3>
      </div>
      <p className="text-sm text-text-secondary mb-4">
        Subscribe to receive our daily betting insights, picks, and analysis delivered to your inbox every morning.
      </p>

      {result && (
        <div className={`mb-4 p-3 rounded-lg ${result.success ? 'bg-accent-green/20 text-accent-green' : 'bg-accent-red/20 text-accent-red'}`}>
          {result.message}
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={currentUser?.email || "Enter your email"}
          className="flex-1 px-4 py-2 bg-bg-primary border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent-blue"
          required
        />
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-accent-blue text-white rounded-lg font-medium hover:bg-accent-blue/80 disabled:opacity-50 transition-colors"
        >
          {loading ? 'Subscribing...' : 'Subscribe'}
        </button>
      </form>
    </div>
  );
}

/**
 * Main Newsletter Page Component
 */
export default function Newsletter() {
  const { date: urlDate } = useParams();
  const navigate = useNavigate();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadNewsletter = async () => {
      setLoading(true);
      setError(null);

      try {
        let response;
        if (urlDate) {
          response = await newsletterService.getByDate(urlDate);
        } else {
          response = await newsletterService.getToday();
        }

        if (response.success && response.newsletter) {
          setNewsletter(response.newsletter);
        } else {
          setError(response.message || 'Newsletter not found');
        }
      } catch (err) {
        console.error('Failed to load newsletter:', err);
        setError(err.response?.data?.detail || 'Failed to load newsletter');
      } finally {
        setLoading(false);
      }
    };

    loadNewsletter();
  }, [urlDate]);

  const navigateDate = (days) => {
    const currentDate = newsletter?.date
      ? new Date(newsletter.date)
      : new Date();
    currentDate.setDate(currentDate.getDate() + days);
    const newDate = currentDate.toISOString().split('T')[0];
    navigate(`/newsletter/${newDate}`);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <ArrowPathIcon className="w-8 h-8 text-accent-blue animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-bg-secondary rounded-xl p-8 text-center border border-border/50">
          <NewspaperIcon className="w-16 h-16 text-text-secondary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary mb-2">Newsletter Not Available</h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => navigateDate(-1)}
              className="px-4 py-2 bg-bg-primary text-text-primary rounded-lg hover:bg-bg-primary/80 transition-colors flex items-center gap-2"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous Day
            </button>
            <button
              onClick={() => navigate('/newsletter')}
              className="px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/80 transition-colors"
            >
              View Today
            </button>
          </div>
        </div>

        <div className="mt-8">
          <SubscribeForm />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex items-center justify-center gap-2 mb-2">
          <BoltIcon className="w-8 h-8 text-accent-blue" />
          <h1 className="text-3xl font-bold text-text-primary">{newsletter?.title || 'The Daily Edge'}</h1>
        </div>
        <p className="text-text-secondary">{newsletter?.subtitle}</p>

        {/* Date Navigation */}
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => navigateDate(-1)}
            className="p-2 rounded-lg bg-bg-secondary hover:bg-bg-secondary/80 transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5 text-text-secondary" />
          </button>
          <div className="flex items-center gap-2 text-accent-blue">
            <CalendarIcon className="w-5 h-5" />
            <span className="font-medium">{formatDate(newsletter?.date)}</span>
          </div>
          <button
            onClick={() => navigateDate(1)}
            className="p-2 rounded-lg bg-bg-secondary hover:bg-bg-secondary/80 transition-colors"
          >
            <ChevronRightIcon className="w-5 h-5 text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      {newsletter?.stats && (
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatsCard
            label="Yesterday's Picks"
            value={newsletter.stats.yesterday_picks || 0}
            subvalue="Total picks made"
          />
          <StatsCard
            label="Wins"
            value={newsletter.stats.yesterday_wins || 0}
            subvalue={`${newsletter.stats.hit_rate?.toFixed(1) || 0}% hit rate`}
            color={newsletter.stats.hit_rate >= 55 ? 'green' : newsletter.stats.hit_rate >= 50 ? 'yellow' : 'red'}
          />
          <StatsCard
            label="Hit Rate"
            value={`${newsletter.stats.hit_rate?.toFixed(1) || 0}%`}
            subvalue="Yesterday's accuracy"
            color={newsletter.stats.hit_rate >= 55 ? 'green' : newsletter.stats.hit_rate >= 50 ? 'yellow' : 'red'}
          />
        </div>
      )}

      {/* Newsletter Sections */}
      <div className="space-y-4 mb-8">
        {newsletter?.sections?.map((section, idx) => (
          <NewsletterSection key={idx} section={section} />
        ))}
      </div>

      {/* Subscribe Form */}
      <SubscribeForm />

      {/* Archive Link */}
      <div className="text-center mt-8">
        <Link
          to="/newsletter/archive"
          className="text-accent-blue hover:text-accent-blue/80 text-sm"
        >
          View Newsletter Archive
        </Link>
      </div>
    </div>
  );
}
